import { ConditionalRender } from '@libs/core';
import { TWidgetCommonOption } from '@libs/types';
import { CoreButtonView, CoreTextView, type TCoreButtonStyleProps, type TCoreButtonViewProps } from '@core/widgets';
import clsx from 'clsx';
import React, { MouseEventHandler } from 'react';
import { TButtonOption } from '../../types/options';
import { isMobileByTouch } from '../../utils/helpers';
import { VisualView } from '../Image/VisualView';
import styles from './styles/buttonPill.module.css';
import { getActiveBgColor, getBoxShadow, getHoverBgColor, getMainColor, getOnMainColor, getPaddingPostfix } from './styles/buttonPillUtils';

type TButtonPillStyleProps = Pick<TCoreButtonStyleProps, 'width' | 'height'>;

export type TButtonPillViewProps = {
    uid?: string;
    label?: string;
    dynamicShadow?: boolean;
    position?: 'floating' | 'inline';
    size?: 'large' | 'medium' | 'small';
    viewBackground?: 'plain' | 'gradient';
    fillContainer?: boolean;
    maxWidth?: string;
    iconName?: string;
    flagIcon?: string;
    iconPosition?: 'left' | 'right';
    hierarchy?: 'primary' | 'secondary' | 'tertiary';
    customBackground?: string;
    customDisabledBackground?: string;
    styleProps?: TButtonPillStyleProps;
    margin?: TButtonOption['margin'];
    /** Whether the document has been scrolled — controls dynamic shadow elevation. */
    documentScrolled?: boolean;
    onClick: MouseEventHandler<HTMLButtonElement>;
    /** Used solely for theme resolution via useThemeHook inside CoreButtonView. Optional. */
    widget?: TCoreButtonViewProps['widget'];
    parentStyleNames?: string[];
    /** Theme style key for the button container. Override to resolve a host-specific palette (e.g. `calculatorSubmitButton`). Defaults to `buttonPill`. */
    styleName?: string;
} & Pick<TWidgetCommonOption, 'isDisabled' | 'isHide'>;

const PADDING_MAP: Record<string, { top: number; right: number; bottom: number; left: number }> = {
    Large: { top: 10, right: 32, bottom: 10, left: 32 },
    LargeIconRight: { top: 10, right: 16, bottom: 10, left: 32 },
    LargeIconLeft: { top: 10, right: 32, bottom: 10, left: 16 },
    Medium: { top: 10, right: 24, bottom: 10, left: 24 },
    MediumIconRight: { top: 10, right: 16, bottom: 10, left: 16 },
    MediumIconLeft: { top: 10, right: 16, bottom: 10, left: 16 },
    Small: { top: 10, right: 16, bottom: 10, left: 16 },
    SmallIconRight: { top: 10, right: 8, bottom: 10, left: 16 },
    SmallIconLeft: { top: 10, right: 16, bottom: 10, left: 8 },
};

/**
 * Composable view for the pill-shaped button used across consumer screens.
 * Owns only rendering logic — accepts all behavior props as pre-resolved inputs
 * from the parent (isDisabled, isHide, onClick, documentScrolled).
 *
 * Use this component when behavior state is already managed one level up,
 * e.g. inside a complex composed widget or a screen-level handler.
 * For server-driven usage, prefer ButtonPillWidget which wires up all hooks.
 */
export const ButtonPillView = ({
    uid,
    label,
    hierarchy = 'primary',
    viewBackground = 'plain',
    iconName,
    flagIcon,
    iconPosition = 'left',
    size: buttonSize = 'large',
    position = 'inline',
    dynamicShadow,
    fillContainer,
    maxWidth,
    customBackground,
    customDisabledBackground,
    margin,
    documentScrolled,
    isDisabled,
    isHide,
    onClick,
    styleProps,
    widget,
    parentStyleNames: parentStyleNamesProp,
    styleName = 'buttonPill',
}: TButtonPillViewProps) => {
    const getPosition = () => {
        const dynamicShadowDefault = dynamicShadow ?? true;
        if (dynamicShadowDefault && documentScrolled && !isDisabled) {
            return 'floating';
        } else {
            return position;
        }
    };

    const iconHierarchy = () => {
        switch (hierarchy) {
            case 'primary':
            case 'secondary':
                return 'emphasised';
            case 'tertiary':
                return 'secondary';
            default:
                return 'emphasised';
        }
    };

    const paddingPostfix = getPaddingPostfix(!!(iconName || flagIcon), iconPosition, buttonSize);
    const boxShadowKey = getBoxShadow(getPosition(), hierarchy, !!isDisabled);

    const coreButtonStyleProps: TCoreButtonStyleProps = {
        main: customBackground ? `var(--color-${customBackground})` : getMainColor(hierarchy, viewBackground, !!isDisabled),
        onMain: getOnMainColor(hierarchy, viewBackground, false),
        fillContainer: fillContainer ?? false,
        maxWidth: maxWidth !== undefined ? maxWidth : isMobileByTouch() ? undefined : 'calc(((100vw - 168px) / 6) * 2 + 24px)',
        height: buttonSize === 'large' ? 56 : buttonSize === 'medium' ? 40 : 28,
        ...styleProps,
        borderRadius: 76,
        padding: PADDING_MAP[paddingPostfix],
        fontStyle: buttonSize === 'small' ? { fontSize: 14, lineHeight: 20 } : { fontSize: 17, lineHeight: 24 },
        margin,
        boxShadow: boxShadowKey === 'Purple' ? 'var(--color-shadow_purple)' : boxShadowKey === 'Grey' ? 'var(--color-shadow_grey)' : undefined,
        cursorDisabled: 'default',
        disabled: {
            main: customDisabledBackground ?? getMainColor(hierarchy, viewBackground, true),
            onMain: getOnMainColor(hierarchy, viewBackground, true),
        },
        hovered: {
            main: getHoverBgColor(hierarchy, viewBackground),
            onMain: getOnMainColor(hierarchy, viewBackground, false),
        },
        pressed: {
            main: getActiveBgColor(hierarchy, viewBackground),
            onMain: getOnMainColor(hierarchy, viewBackground, false),
        },
    };

    const coreTextStyleProps = {
        main: 'inherit',
    };

    const parentStyleNames = parentStyleNamesProp ?? [];

    const resolvedWidget: TCoreButtonViewProps['widget'] = widget ?? ({ uid: uid ?? '', data: { text: label }, options: {} } as TCoreButtonViewProps['widget']);

    return (
        <CoreButtonView
            widget={resolvedWidget}
            text={label}
            isDisabled={isDisabled}
            isHide={isHide}
            styleProps={coreButtonStyleProps}
            parentStyleNames={parentStyleNames}
            styleName={styleName}
            onClick={onClick}
        >
            <ConditionalRender condition={!!(iconName || flagIcon)}>
                <div className={clsx(iconPosition === 'right' && styles.rightOrder)}>
                    <VisualView
                        isDisabled={isDisabled}
                        parentStyleNames={[...parentStyleNames, 'buttonPillIcon']}
                        iconHierarchy={iconHierarchy()}
                        iconVariant={'simple'}
                        size={buttonSize === 'small' ? 'xxSmall' : 'small'}
                        iconName={iconName}
                        flagIcon={flagIcon}
                    />
                </div>
            </ConditionalRender>
            <CoreTextView text={label} as="span" parentStyleNames={parentStyleNames} styleName={'buttonPillText'} styleProps={coreTextStyleProps} />
        </CoreButtonView>
    );
};
